/**
 * Utility functions for handling font loading with SplitText
 */

/**
 * Wait for fonts to be loaded before proceeding
 * This prevents the "SplitText called before fonts loaded" warning
 */
export function waitForFonts(): Promise<void> {
  return new Promise((resolve) => {
    // Check if document.fonts is available (modern browsers)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        resolve();
      });
    } else {
      // Fallback for older browsers - wait a bit for fonts to load
      setTimeout(() => {
        resolve();
      }, 100);
    }
  });
}

function checkIfInnerElement(element: Element): Element {
  // data-wf--typography-heading--font-style
  // check if element has data-wf--typography-heading--font-style
  if (element.hasAttribute("data-wf--typography-heading--font-style")) {
    return element.querySelector("*") as Element;
  }
  return element;
}

/**
 * Create SplitText with font loading safety
 * @param element - The element to split
 * @param options - SplitText options
 * @returns Promise that resolves to the SplitText instance
 */
export async function safeSplit(element: Element, options: any): Promise<any> {
  await waitForFonts();

  // Import SplitText dynamically to avoid circular dependencies
  // const { SplitText } = await import("@lib/gsap");
  const elementToSplit = checkIfInnerElement(element);
  return nestedLinesSplit(elementToSplit, options);
}

/**
 * Create multiple SplitText instances safely
 * @param elements - Array of elements and their options
 * @returns Promise that resolves to an array of SplitText instances
 */
export async function safeSplitMany(
  elements: Array<{ element: Element; options: any }>
): Promise<any[]> {
  await waitForFonts();

  // Import SplitText dynamically to avoid circular dependencies
  // const { SplitText } = await import("@lib/gsap");

  return Promise.all(
    elements.map(({ element, options }) => {
      const elementToSplit = checkIfInnerElement(element);

      return nestedLinesSplit(elementToSplit, options);
    })
  );
}

async function nestedLinesSplit(target: any, vars: any): Promise<any> {
  const { SplitText } = await import("@lib/gsap");
  const { gsap } = await import("gsap");
  target = gsap.utils.toArray(target);
  if (target.length > 1) {
    let splits = target.map((t: any) => nestedLinesSplit(t, vars)),
      result = splits[0],
      resultRevert = result.revert;
    result.lines = splits.reduce(
      (acc: any, cur: any) => acc.concat(cur.lines),
      []
    );
    result.revert = () =>
      splits.forEach((s: any) => (s === result ? resultRevert() : s.revert()));
    return result;
  }
  target = target[0];
  let contents = target.innerHTML;
  gsap.utils.toArray(target.children).forEach((child: any) => {
    let split = new SplitText(child as any, { type: "lines" });
    split.lines.forEach((line) => {
      let clone = child.cloneNode(false) as any;
      clone.innerHTML = line.innerHTML;
      target.insertBefore(clone, child);
    });
    target.removeChild(child);
  });
  let split = new SplitText(target, vars),
    originalRevert = split.revert;
  split.revert = () => {
    originalRevert.call(split);
    target.innerHTML = contents;
  };
  return split;
}
